import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Tag, X, Save,
  Percent, DollarSign, BarChart3, Copy, ToggleLeft, ToggleRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_OPTIONS = [
  { value: 'herb-savory', label: 'Herb & Savory' },
  { value: 'citrus-garlic', label: 'Citrus Garlic' },
  { value: 'smoky-savory', label: 'Smoky Savory' },
  { value: 'seafood-seasoning', label: 'Seafood Seasoning' },
  { value: 'full-set', label: 'Full Set Bundle' },
];

const emptyForm = {
  code: '',
  discount_type: 'percentage',
  discount_value: '',
  min_order_amount: '',
  applicable_products: [],
  max_uses: '',
  one_per_customer: false,
  start_date: '',
  end_date: '',
  active: true,
};

const AdminDiscountsPage = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [codes, setCodes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    const headers = getAuthHeaders();
    try {
      const [codesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/discounts`, { headers }),
        axios.get(`${API}/admin/discounts/analytics`, { headers }),
      ]);
      setCodes(codesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to load discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (code = null) => {
    if (code) {
      setEditingCode(code);
      setFormData({
        code: code.code || '',
        discount_type: code.discount_type || 'percentage',
        discount_value: code.discount_value || '',
        min_order_amount: code.min_order_amount || '',
        applicable_products: code.applicable_products || [],
        max_uses: code.max_uses || '',
        one_per_customer: code.one_per_customer || false,
        start_date: code.start_date ? code.start_date.slice(0, 16) : '',
        end_date: code.end_date ? code.end_date.slice(0, 16) : '',
        active: code.active !== false,
      });
    } else {
      setEditingCode(null);
      setFormData(emptyForm);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.discount_value) {
      toast.error('Code and discount value are required');
      return;
    }
    setSaving(true);
    const headers = getAuthHeaders();
    const payload = {
      ...formData,
      discount_value: parseFloat(formData.discount_value),
      min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : 0,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : 0,
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
    };

    try {
      if (editingCode) {
        await axios.put(`${API}/admin/discounts/${editingCode.id}`, payload, { headers });
        toast.success('Discount code updated');
      } else {
        await axios.post(`${API}/admin/discounts`, payload, { headers });
        toast.success('Discount code created');
      }
      setShowModal(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete code "${code.code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/discounts/${code.id}`, { headers: getAuthHeaders() });
      toast.success('Discount code deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await axios.put(`${API}/admin/discounts/${code.id}`, { active: !code.active }, { headers: getAuthHeaders() });
      fetchAll();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  const toggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      applicable_products: prev.applicable_products.includes(productId)
        ? prev.applicable_products.filter(p => p !== productId)
        : [...prev.applicable_products, productId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totals = analytics?.totals || {};

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-discounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Discount Codes</h1>
          <p className="text-[#888] text-sm">Manage promo codes and track performance</p>
        </div>
        <Button onClick={() => openModal()} className="bg-[#D4540A] hover:bg-[#B84709] text-white" data-testid="add-discount-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Code
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-[#888] text-xs uppercase">Total Codes</p>
          <p className="text-white text-2xl font-bold mt-1">{codes.length}</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-[#888] text-xs uppercase">Total Uses</p>
          <p className="text-white text-2xl font-bold mt-1">{totals.total_uses || 0}</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-[#888] text-xs uppercase">Total Discounts</p>
          <p className="text-white text-2xl font-bold mt-1">${(totals.total_discount_given || 0).toFixed(2)}</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-[#888] text-xs uppercase">Revenue from Codes</p>
          <p className="text-white text-2xl font-bold mt-1">${(totals.total_revenue_from_discounted_orders || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Codes List */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a]">
        {codes.length === 0 ? (
          <div className="text-center py-16">
            <Tag className="w-12 h-12 text-[#555] mx-auto mb-3" />
            <p className="text-[#888]">No discount codes yet</p>
            <p className="text-[#555] text-sm">Create your first promo code to start running promotions</p>
          </div>
        ) : (
          <div className="divide-y divide-[#2a2a2a]">
            {codes.map((code) => (
              <div key={code.id} className="p-4 flex items-center gap-4 hover:bg-[#222] transition-colors" data-testid={`discount-row-${code.code}`}>
                {/* Code + type */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <button onClick={() => copyCode(code.code)} className="flex items-center gap-1 hover:opacity-80">
                      <span className="text-[#D4540A] font-mono font-bold text-lg">{code.code}</span>
                      <Copy className="w-3 h-3 text-[#666]" />
                    </button>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${code.active ? 'bg-emerald-900/30 text-emerald-400' : 'bg-red-900/30 text-red-400'}`}>
                      {code.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#888]">
                    <span className="flex items-center gap-1">
                      {code.discount_type === 'percentage' ? <Percent className="w-3 h-3" /> : <DollarSign className="w-3 h-3" />}
                      {code.discount_type === 'percentage' ? `${code.discount_value}% off` : `$${code.discount_value} off`}
                    </span>
                    {code.min_order_amount > 0 && <span>Min ${code.min_order_amount}</span>}
                    {code.max_uses > 0 && <span>Max {code.max_uses} uses</span>}
                    {code.one_per_customer && <span>1 per customer</span>}
                    {code.applicable_products?.length > 0 && <span>{code.applicable_products.length} products</span>}
                    {code.end_date && <span>Expires {new Date(code.end_date).toLocaleDateString()}</span>}
                  </div>
                </div>

                {/* Usage stats */}
                <div className="text-right hidden sm:block">
                  <p className="text-white font-medium">{code.times_used || 0} uses</p>
                  <p className="text-[#666] text-xs">${(code.total_discount_given || 0).toFixed(2)} given</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button onClick={() => handleToggleActive(code)} className="p-2 hover:bg-[#333] rounded-lg transition-colors" title={code.active ? 'Deactivate' : 'Activate'}>
                    {code.active ? <ToggleRight className="w-4 h-4 text-emerald-400" /> : <ToggleLeft className="w-4 h-4 text-[#666]" />}
                  </button>
                  <button onClick={() => openModal(code)} className="p-2 hover:bg-[#333] rounded-lg transition-colors">
                    <Edit2 className="w-4 h-4 text-[#888]" />
                  </button>
                  <button onClick={() => handleDelete(code)} className="p-2 hover:bg-[#333] rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="discount-modal">
            <div className="flex items-center justify-between p-5 border-b border-[#2a2a2a]">
              <h2 className="text-lg font-bold text-white">{editingCode ? 'Edit Discount Code' : 'New Discount Code'}</h2>
              <button onClick={() => setShowModal(false)} className="text-[#888] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Code */}
              <div>
                <Label className="text-white">Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. SUMMER20"
                  className="bg-[#111] border-[#333] text-white mt-1 font-mono"
                  data-testid="discount-code-input"
                />
              </div>

              {/* Type + Value */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Type *</Label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_type: e.target.value }))}
                    className="w-full bg-[#111] border border-[#333] text-white mt-1 rounded-md px-3 py-2 text-sm"
                    data-testid="discount-type-select"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-white">Value *</Label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_value: e.target.value }))}
                    placeholder={formData.discount_type === 'percentage' ? 'e.g. 15' : 'e.g. 5.00'}
                    className="bg-[#111] border-[#333] text-white mt-1"
                    data-testid="discount-value-input"
                  />
                </div>
              </div>

              {/* Min order + Max uses */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Min Order Amount ($)</Label>
                  <Input
                    type="number"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_order_amount: e.target.value }))}
                    placeholder="0 = no minimum"
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Max Total Uses</Label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.target.value }))}
                    placeholder="0 = unlimited"
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
              </div>

              {/* One per customer */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, one_per_customer: !prev.one_per_customer }))}
                  className={`w-10 h-6 rounded-full transition-colors flex items-center ${formData.one_per_customer ? 'bg-[#D4540A] justify-end' : 'bg-[#333] justify-start'}`}
                  data-testid="one-per-customer-toggle"
                >
                  <div className="w-4 h-4 bg-white rounded-full mx-1" />
                </button>
                <Label className="text-white cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, one_per_customer: !prev.one_per_customer }))}>
                  One use per customer
                </Label>
              </div>

              {/* Applicable Products */}
              <div>
                <Label className="text-white mb-2 block">Applicable Products</Label>
                <p className="text-[#666] text-xs mb-2">Leave all unchecked for whole-order discount</p>
                <div className="flex flex-wrap gap-2">
                  {PRODUCT_OPTIONS.map(p => (
                    <button
                      key={p.value}
                      onClick={() => toggleProduct(p.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                        formData.applicable_products.includes(p.value)
                          ? 'bg-[#D4540A] border-[#D4540A] text-white'
                          : 'bg-transparent border-[#333] text-[#888] hover:border-[#666]'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Start Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">End Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, active: !prev.active }))}
                  className={`w-10 h-6 rounded-full transition-colors flex items-center ${formData.active ? 'bg-emerald-600 justify-end' : 'bg-[#333] justify-start'}`}
                >
                  <div className="w-4 h-4 bg-white rounded-full mx-1" />
                </button>
                <Label className="text-white">Active</Label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-5 border-t border-[#2a2a2a]">
              <Button variant="outline" onClick={() => setShowModal(false)} className="border-[#333] text-[#888]">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#D4540A] hover:bg-[#B84709] text-white" data-testid="save-discount-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Code'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDiscountsPage;
